import { Box, Button, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Wrapper } from "./styles";
import { hallSelector } from "../../../redux/hallReducer";
import { convertToUtc, date } from "../../../utils/date";
import DateTimeRangePicker from "../../Common/DateTimeRangePicker";
import Preloader from "../../Common/Preloader";
import { useTranslation } from "react-i18next";
import { usePaymentsBalancesMutation, usePaymentsReportQuery } from "../../../api/reports";
import { HallReportProps, PaymentsReportResponseItem } from "../../Halls/Form/interface";
import ModifyForm from "./modify-form";
import WithdrawForm from "./withdraw-form";

const PaymentsReports: React.FC = () => {
  const [t] = useTranslation();
  const { halls: { data: halls }, } = useSelector(hallSelector);
  const hallsNames: { [id: number]: string } = useMemo(() => halls.reduce((p, c) => ({ ...p, [c.id]: c.name }), {}), [halls]);
  const hallsIds = useMemo(() => halls.map((item) => item.id), [halls]);
  const [form, setForm] = useState<HallReportProps>({
    from: convertToUtc(date() + " 00:00:00"),
    to: convertToUtc(date() + " 23:59:59"),
    id: hallsIds,
  });
  useEffect(() => {
    setForm(form => ({ ...form, id: hallsIds }));
  }, [hallsIds, setForm]);
  const { data: report, isLoading } = usePaymentsReportQuery(form, { skip: form.id.length === 0 });
  const [getBalance, { isLoading: isLoadingBalance }] = usePaymentsBalancesMutation();
  const onApply = useCallback((_: any, picker: any) => setForm({ ...form, from: convertToUtc(picker.startDate._d), to: convertToUtc(picker.endDate._d) }), [form, setForm]);
  const reportHalls: [number, [string, { [type: number]: PaymentsReportResponseItem }][]][] | null = useMemo(() => {
    if (!report || !report.halls) {
      return null;
    }
    return Object.entries(report.halls).map(([hallId, serviceInfo]) => [+hallId, Object.entries(serviceInfo)]);
  }, [report]);
  return <Wrapper>
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
      <DateTimeRangePicker onApply={onApply} />
    </Box>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>id</TableCell>
          <TableCell>{t("ru.reports.fields.hall")}</TableCell>
          <TableCell>Ввод</TableCell>
          <TableCell>Вывод</TableCell>
          <TableCell>Баланс</TableCell>
        </TableRow>
      </TableHead>
      {isLoading ?
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5}>
              <Preloader absolute={false} />
            </TableCell>
          </TableRow>
        </TableFooter> :
        reportHalls && reportHalls.map(([hallId, serviceInfo]) => {
          const debit = serviceInfo.reduce((p, c) => p + (0 in c[1] ? c[1][0].sum : 0), 0) / 100;
          const credit = serviceInfo.reduce((p, c) => p + (1 in c[1] ? c[1][1].sum : 0), 0) / 100;
          return <TableBody key={hallId}>
            <TableRow>
              <TableCell>{hallId}</TableCell>
              <TableCell>{hallsNames[hallId] ?? `$${hallId}`}</TableCell>
              <TableCell>{debit}</TableCell>
              <TableCell>{credit}</TableCell>
              <TableCell>{debit - credit}</TableCell>
            </TableRow>
            {serviceInfo.map(([service, typeInfo], idx) => {
              const debit = 0 in typeInfo ? typeInfo[0].sum / 100 : 0;
              const credit = 1 in typeInfo ? typeInfo[1].sum / 100 : 0;
              return <TableRow key={service} style={{ backgroundColor: idx % 2 === 0 ? "lightgray" : "#bbb" }}>
                <TableCell></TableCell>
                <TableCell>{service === "false" ? "Не опознан" : service}</TableCell>
                <TableCell>{debit}</TableCell>
                <TableCell>{credit}</TableCell>
                <TableCell>{debit - credit}</TableCell>
              </TableRow>
            })}
          </TableBody>
        })}
      {(!reportHalls || reportHalls.length === 0) && <TableFooter>
        <TableRow>
          <TableCell colSpan={5}>{t("ru.common.no_data")}</TableCell>
        </TableRow>
      </TableFooter>}
    </Table>
    <h1 style={{ marginTop: "20px" }}>Балансы</h1>
    {!!report?.total && <Table>
      <TableHead>
        <TableRow>
          <TableCell>Платёжка</TableCell>
          <TableCell>Балансы</TableCell>
          <TableCell>Обновить</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(report.total).map(([service, serviceBalance]) => {
          const balances = Object.entries(serviceBalance).map(([currency, balance]) => `${balance} ${currency}`).join(", ");
          return <TableRow key={service}>
            <TableCell>{service === "false" ? "Не опознан" : service}</TableCell>
            <TableCell>{balances}</TableCell>
            <TableCell style={{gap: "2px", display: "flex"}}>
              {!!report && service in report.actions && report.actions[service].includes("update") && <Button disabled={isLoadingBalance} variant="contained" color="primary" onClick={() => getBalance(service)}>Обновить</Button>}
              {!!report && service in report.actions && report.actions[service].includes("modify") && <ModifyForm service={service} balances={serviceBalance} />}
              {!!report && service in report.actions && report.actions[service].includes("withdraw") && <WithdrawForm service={service} balances={serviceBalance} />}
            </TableCell>
          </TableRow>
        })}
      </TableBody>
    </Table>}
  </Wrapper>;
}

export default PaymentsReports;
